/* iter-reveal2 — Admin Notifications inbox (Bell + dropdown).
 *
 * Renders a bell icon with unread count badge in the AdminHub header;
 * clicking pops a dropdown that lists the latest notifications with
 * per-row "Mark read" and "Dismiss" actions. Refreshes every 30 s.
 *
 * iter-bookkeeper-nudge fix · Sep-2026 — this used to require an
 * `adminToken` prop sent as an `Authorization: Bearer` header, but
 * admin auth moved to an HttpOnly `admin_session` cookie long ago
 * (see `lib/api.js` — `adminAuth` has no `.token` anymore). That made
 * every call here silently no-op everywhere it was mounted. Switched
 * to `withCredentials: true` like the rest of the admin API calls;
 * the `adminToken` prop is now unused but harmless if still passed.
 */
import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, CheckCheck, X, Store, Loader2, ShieldCheck, ExternalLink, Landmark, Cpu } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const KIND_META = {
  genre_auto_revealed:              { icon: Store,       tint: '#6A2E2E', label: 'Genre auto-revealed' },
  reader_review_submitted:          { icon: ShieldCheck, tint: '#B45309', label: 'Review submitted' },
  reader_review_assigned:           { icon: ShieldCheck, tint: '#0369A1', label: 'Review assigned' },
  reader_review_reassigned:         { icon: ShieldCheck, tint: '#0369A1', label: 'Review reassigned' },
  reader_review_approved:           { icon: ShieldCheck, tint: '#047857', label: 'Review approved' },
  reader_review_changes_requested:  { icon: ShieldCheck, tint: '#B45309', label: 'Changes requested' },
  reader_review_rejected:           { icon: ShieldCheck, tint: '#BE123C', label: 'Review rejected' },
  // Sep-2026 — Ledger Clerk approval nudge (see ledger_service.py).
  gl_entry_approved:                { icon: Landmark,    tint: '#047857', label: 'Entry approved' },
  gl_entry_rejected:                { icon: Landmark,    tint: '#BE123C', label: 'Entry rejected' },
  // Sep-2026 — Weekly LLM model-catalog freshness nudge (model_catalog_service.py).
  model_catalog_stale:              { icon: Cpu,         tint: '#B45309', label: 'Model catalog' },
};

const FILTERS = [
  { key: '',                    label: 'All' },
  { key: 'reader_review_',      label: 'Reviews' },
  { key: 'gl_entry_',           label: 'Ledger' },
  { key: 'model_catalog_',      label: 'Models' },
];

export default function AdminNotificationsBell({ adminToken } = {}) {
  const [open, setOpen]         = useState(false);
  const [rows, setRows]         = useState([]);
  const [unread, setUnread]     = useState(0);
  const [loading, setLoading]   = useState(false);
  const [filter, setFilter]     = useState('');
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API}/admin/notifications?limit=30${filter ? `&kind_prefix=${filter}` : ''}`;
      const r = await axios.get(url, { withCredentials: true });
      setRows(r.data?.notifications || []);
      setUnread(r.data?.unread_count || 0);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, [load]);

  // Click-outside to close.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/notifications/${id}/read`, null, { withCredentials: true });
      load();
    } catch { toast.error('Could not mark read.'); }
  };
  const dismiss = async (id) => {
    try {
      await axios.post(`${API}/admin/notifications/${id}/dismiss`, null, { withCredentials: true });
      load();
    } catch { toast.error('Dismiss failed.'); }
  };

  return (
    <div ref={wrapRef} className="relative" data-testid="admin-notifications-bell-wrap">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Notifications"
        data-testid="admin-notifications-bell"
        className="relative inline-flex h-8 w-8 items-center justify-center rounded-full text-neutral-600 hover:bg-neutral-100"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span
            className="absolute -right-0.5 -top-0.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-rose-600 px-1 text-[9px] font-bold text-white"
            data-testid="admin-notifications-badge"
          >
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
      {open && (
        <div
          className="absolute right-0 z-30 mt-2 w-[380px] rounded-md border border-neutral-200 bg-white shadow-xl"
          data-testid="admin-notifications-panel"
        >
          <div className="flex items-center justify-between border-b border-neutral-100 px-3 py-2">
            <div className="text-[13px] font-semibold text-neutral-900">Notifications</div>
            <div className="text-[10.5px] text-neutral-500">
              {loading ? <Loader2 className="inline h-3 w-3 animate-spin" /> : `${unread} unread`}
            </div>
          </div>
          <div className="flex items-center gap-1 border-b border-neutral-100 px-3 py-1.5">
            {FILTERS.map((f) => (
              <button
                key={f.key || 'all'}
                type="button"
                onClick={() => setFilter(f.key)}
                data-testid={`admin-notif-filter-${f.key || 'all'}`}
                className={
                  'rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-wider transition ' +
                  (filter === f.key
                    ? 'border-oxblood-600 bg-oxblood-600 text-white'
                    : 'border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50')
                }
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {rows.length === 0 ? (
              <div className="px-3 py-8 text-center text-[11.5px] text-neutral-500"
                    data-testid="admin-notifications-empty">
                Nothing new — you're all caught up.
              </div>
            ) : (
              rows.map((n) => {
                const meta = KIND_META[n.kind] || { icon: Bell, tint: '#5B5348', label: n.kind };
                const Icon = meta.icon;
                const isUnread = !n.read_at;
                return (
                  <div
                    key={n.id}
                    className={
                      'group flex items-start gap-2 border-b border-neutral-100 px-3 py-2 last:border-b-0 '
                      + (isUnread ? 'bg-neutral-50/60' : '')
                    }
                    data-testid={`admin-notification-${n.id}`}
                  >
                    <div className="mt-0.5 rounded-full p-1"
                          style={{ backgroundColor: `${meta.tint}18`, color: meta.tint }}>
                      <Icon className="h-3 w-3" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1">
                        <div className="line-clamp-1 flex-1 text-[12px] font-semibold text-neutral-900">
                          {n.title}
                        </div>
                        {isUnread && (
                          <span className="inline-flex h-1.5 w-1.5 shrink-0 rounded-full bg-oxblood-600"
                                aria-label="unread" />
                        )}
                      </div>
                      <p className="mt-0.5 line-clamp-3 text-[11px] leading-snug text-neutral-600">
                        {n.body}
                      </p>
                      {n.action_hint && (
                        <p className="mt-0.5 text-[10.5px] italic text-neutral-500">{n.action_hint}</p>
                      )}
                      {n.kind === 'genre_auto_revealed' && n.genre_slug && (
                        <div className="mt-1 flex gap-1">
                          <a
                            href={`/bookstore/g/${n.genre_slug}`}
                            target="_blank" rel="noreferrer"
                            className="text-[10.5px] font-semibold text-oxblood-700 hover:underline"
                            data-testid={`admin-notification-${n.id}-view-shelf`}
                          >
                            View shelf →
                          </a>
                        </div>
                      )}
                      {n.kind && n.kind.startsWith('reader_review_') && (
                        <div className="mt-1 flex gap-1">
                          <a
                            href="/admin/operations?tab=reader-review"
                            className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-oxblood-700 hover:underline"
                            data-testid={`admin-notification-${n.id}-view-queue`}
                          >
                            View queue <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        </div>
                      )}
                      {n.kind && n.kind.startsWith('gl_entry_') && (
                        <div className="mt-1 flex gap-1">
                          <a
                            href="/admin/finance?tab=bookkeeping"
                            className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-oxblood-700 hover:underline"
                            data-testid={`admin-notification-${n.id}-view-bookkeeping`}
                          >
                            View in Bookkeeping <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        </div>
                      )}
                      {n.kind === 'model_catalog_stale' && (
                        <div className="mt-1 flex gap-1">
                          <a
                            href="/admin/operations?tab=models"
                            className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-oxblood-700 hover:underline"
                            data-testid={`admin-notification-${n.id}-view-models`}
                          >
                            Review models <ExternalLink className="h-2.5 w-2.5" />
                          </a>
                        </div>
                      )}
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-[9.5px] text-neutral-400">
                          {n.created_at ? new Date(n.created_at).toLocaleString() : ''}
                        </span>
                        <div className="flex items-center gap-1 opacity-70 group-hover:opacity-100">
                          {isUnread && (
                            <button
                              type="button"
                              onClick={() => markRead(n.id)}
                              className="rounded p-0.5 text-neutral-500 hover:bg-neutral-100 hover:text-emerald-700"
                              data-testid={`admin-notification-${n.id}-read`}
                              title="Mark read"
                            >
                              <CheckCheck className="h-3 w-3" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => dismiss(n.id)}
                            className="rounded p-0.5 text-neutral-500 hover:bg-neutral-100 hover:text-rose-600"
                            data-testid={`admin-notification-${n.id}-dismiss`}
                            title="Dismiss"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
